import React, { useEffect } from 'react';
import { Link, useLocation } from 'wouter';
import { 
  Lightbulb, 
  AlertTriangle, 
  Info, 
  ArrowLeft, 
  FileText,
  Book,
  CheckCircle,
  PlusCircle,
  MinusCircle
} from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import Slideshow, { SlideContent } from '@/components/lectures/Slideshow';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';

// KeyTakeaway component
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <Lightbulb className="text-blue-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-blue-700 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

// Clinical Note component
const ClinicalNote = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <AlertTriangle className="text-amber-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-amber-800 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

// Information box component
const InfoBox = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-slate-50 border-l-4 border-slate-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <Info className="text-slate-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-slate-700 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

const lectureSlides: SlideContent[] = [
  {
    title: "The Paradigm Shift in Senescence Monitoring",
    content: (
      <>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">From Theory to Practice: Clinically Quantifying Senescence</h2>
          <p className="text-lg mb-4">
            Friends, we've often discussed cellular senescence and its biomarkers like p16 and SASP as foundational concepts. 
            The major update is that we've moved beyond research-based markers. We now have a CLIA-validated, clinically actionable blood test—<strong>SapereX</strong>—that provides a two-dimensional view of a patient's senescence status.
          </p>
        </div>

        <div className="mb-6">
          <p className="mb-4">This test analyzes T-cells, which act as systemic surveyors, to give us two critical outputs:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">1. Senescence Load</h3>
              <p className="text-blue-700 text-sm">
                Using <strong>p16<sup>INK4a</sup></strong> expression, it calculates a "Molecular Age," showing the cellular aging burden.
              </p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">2. Immune Resilience</h3>
              <p className="text-green-700 text-sm">
                It measures markers of immune exhaustion (<strong>LAG3, CD244</strong>) and competence (<strong>CD28</strong>) to tell us how well the patient's immune system can clear these senescent cells.
              </p>
            </div>
          </div>

          <p className="mb-6">
            This allows us to stratify patients into four distinct clinical phenotypes, moving from a simple "high or low" burden to a more nuanced, actionable assessment.
          </p>
        </div>

        {/* SapereX Clinical Quadrants Diagram */}
        <div className="flex justify-center my-8">
          <div className="bg-slate-50 p-6 rounded-lg border">
            <svg width="600" height="400" viewBox="0 0 600 400" className="max-w-full h-auto">
              <style>
                {`
                  .quadrant-text { font-family: Arial, sans-serif; font-size: 14px; text-anchor: middle; }
                  .axis-label { font-family: Arial, sans-serif; font-size: 16px; font-weight: bold; text-anchor: middle; }
                  .title { font-family: Arial, sans-serif; font-size: 18px; font-weight: bold; text-anchor: middle; }
                `}
              </style>

              {/* Axes */}
              <line x1="300" y1="50" x2="300" y2="350" stroke="#333" strokeWidth="2"/>
              <line x1="50" y1="200" x2="550" y2="200" stroke="#333" strokeWidth="2"/>

              {/* Axis Labels */}
              <text x="300" y="30" className="title">SapereX Clinical Quadrants</text>
              <text x="300" y="380" className="axis-label">Senescence Load (p16 Molecular Age)</text>
              <text x="170" y="195" className="axis-label" fill="green">Low</text>
              <text x="430" y="195" className="axis-label" fill="red">High</text>
              <text x="280" y="110" className="axis-label" transform="rotate(-90 280,110)" fill="green">High</text>
              <text x="280" y="290" className="axis-label" transform="rotate(-90 280,290)" fill="red">Low</text>
              <text x="40" y="205" className="axis-label" transform="rotate(-90 40,205)">Immune Resilience</text>

              {/* Quadrant 1: Optimal */}
              <rect x="50" y="50" width="250" height="150" fill="#e6f7ff" stroke="#b3e0ff" strokeWidth="1"/>
              <text x="175" y="110" className="quadrant-text" fontWeight="bold">1. Optimal State</text>
              <text x="175" y="130" className="quadrant-text">Low Burden, Effective Clearance</text>
              <text x="175" y="150" className="quadrant-text">Action: Monitor, Maintain Lifestyle</text>

              {/* Quadrant 2: Impaired Clearance */}
              <rect x="50" y="200" width="250" height="150" fill="#fffbe6" stroke="#ffeeba" strokeWidth="1"/>
              <text x="175" y="260" className="quadrant-text" fontWeight="bold">2. Impaired Clearance</text>
              <text x="175" y="280" className="quadrant-text">Low Burden, Poor Immunity</text>
              <text x="175" y="300" className="quadrant-text">Action: Immune Support (IL-7, Exercise)</text>

              {/* Quadrant 3: Acute Senescence */}
              <rect x="300" y="50" width="250" height="150" fill="#fff0f0" stroke="#ffcccc" strokeWidth="1"/>
              <text x="425" y="110" className="quadrant-text" fontWeight="bold">3. Acute/Recent Senescence</text>
              <text x="425" y="130" className="quadrant-text">High Burden, Intact Clearance</text>
              <text x="425" y="150" className="quadrant-text">Action: Support Clearance, Re-test</text>

              {/* Quadrant 4: Highest Priority */}
              <rect x="300" y="200" width="250" height="150" fill="#ff4d4d" stroke="#cc0000" strokeWidth="1.5"/>
              <text x="425" y="260" className="quadrant-text" fontWeight="bold" fill="white">4. Highest Priority</text>
              <text x="425" y="280" className="quadrant-text" fill="white">High Burden, Failed Clearance</text>
              <text x="425" y="300" className="quadrant-text" fill="white">Action: Senolytics + Autophagy</text>
            </svg>
          </div>
        </div>

        <KeyTakeaway>
          SapereX represents the first CLIA-validated test that moves senescence monitoring from research to clinical practice. 
          By measuring both senescent cell burden and immune clearance capacity, it provides actionable stratification for precision interventions.
        </KeyTakeaway>
      </>
    ),
  },
  {
    title: "Clinical Integration & The SHIFT Protocol Update",
    content: (
      <>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">A Closed-Loop Protocol for Managing Senescence</h2>
          <p className="text-lg mb-4">
            The key practice change is moving from reactive to proactive, data-driven management. We can now integrate this test directly into our SHIFT framework, creating a closed-loop system for the <strong>'H' (Homeostasis)</strong> pillar.
          </p>
        </div>

        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Updated Clinical Algorithm:</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-2">1. Screen</h4>
              <p className="text-blue-700 text-sm">Establish a baseline with SapereX.</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-800 mb-2">2. Stratify</h4>
              <p className="text-green-700 text-sm">Use the matrix to identify the patient's phenotype.</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h4 className="font-semibold text-orange-800 mb-2">3. Intervene</h4>
              <p className="text-orange-700 text-sm">For highest-risk patients (Quadrant 4), initiate targeted protocol.</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h4 className="font-semibold text-purple-800 mb-2">4. Track</h4>
              <p className="text-purple-700 text-sm">Re-test in 12 weeks to measure response and titrate therapy.</p>
            </div>
          </div>
        </div>

        <ClinicalNote>
          <h3 className="font-semibold mb-2">Interpretation Pearls:</h3>
          <ul className="space-y-1">
            <li><strong>Lymphopenia:</strong> Falsely low p16 in patients post-chemo. Corroborate with a SASP marker like suPAR.</li>
            <li><strong>High p16 / High Resilience:</strong> Suspect an <em>acute</em> trigger (like recent surgery or infection). Don't jump to senolytics; support the immune system first.</li>
          </ul>
        </ClinicalNote>

        {/* Clinical Workflow Diagram */}
        <div className="flex justify-center my-8">
          <div className="bg-slate-50 p-6 rounded-lg border">
            <svg width="650" height="300" viewBox="0 0 650 300" className="max-w-full h-auto">
              <style>
                {`
                  .flow-text { font-family: Arial, sans-serif; font-size: 14px; text-anchor: middle; }
                  .flow-title { font-family: Arial, sans-serif; font-size: 16px; font-weight: bold; text-anchor: middle; }
                  .arrow { fill: #333; }
                `}
              </style>

              {/* Step 1: Test */}
              <rect x="20" y="100" width="120" height="60" rx="10" fill="#e6f7ff" stroke="#007bff"/>
              <text x="80" y="125" className="flow-text">1. Baseline</text>
              <text x="80" y="145" className="flow-text">SapereX Test</text>

              {/* Arrow 1 */}
              <line x1="140" y1="130" x2="170" y2="130" stroke="#333" strokeWidth="2"/>
              <polygon points="170,125 180,130 170,135" className="arrow"/>

              {/* Step 2: Stratify */}
              <rect x="180" y="100" width="120" height="60" rx="10" fill="#fffbe6" stroke="#ffc107"/>
              <text x="240" y="125" className="flow-text">2. Stratify via</text>
              <text x="240" y="145" className="flow-text">Quadrant</text>

              {/* Arrow 2 */}
              <line x1="300" y1="130" x2="330" y2="130" stroke="#333" strokeWidth="2"/>
              <polygon points="330,125 340,130 330,135" className="arrow"/>

              {/* Step 3: Intervene (Decision) */}
              <path d="M340 130 L370 100 L490 100 L520 130 L490 160 L370 160 Z" fill="#ffe6e6" stroke="#dc3545"/>
              <text x="430" y="125" className="flow-text" fontWeight="bold">IF Quadrant 4:</text>
              <text x="430" y="145" className="flow-text">(High p16, Low Resilience)</text>

              {/* Arrow 3 */}
              <line x1="430" y1="160" x2="430" y2="190" stroke="#333" strokeWidth="2"/>
              <polygon points="425,190 430,200 435,190" className="arrow"/>

              {/* Step 4: Action */}
              <rect x="340" y="200" width="180" height="80" rx="10" fill="#d4edda" stroke="#28a745"/>
              <text x="430" y="220" className="flow-text" fontWeight="bold">3. Intervene (12 weeks)</text>
              <text x="430" y="240" className="flow-text">• Senolytic: D+Q (2 days/month)</text>
              <text x="430" y="255" className="flow-text">• Autophagy: Rapamycin (5mg/wk)</text>

              {/* Loop back arrow */}
              <path d="M340 240 Q 200 280 145 150" stroke="#333" strokeWidth="2" fill="none" strokeDasharray="5,5"/>
              <polygon points="145,155 140,145 150,145" className="arrow"/>
              <text x="240" y="200" className="flow-text">4. Re-test & Titrate</text>
            </svg>
          </div>
        </div>

        <KeyTakeaway>
          This closed-loop protocol transforms senescence management from a theoretical concept to a measurable, 
          trackable intervention within the SHIFT framework, specifically anchoring the Homeostasis pillar.
        </KeyTakeaway>
      </>
    ),
  },
  {
    title: "Deeper Insights & The Big Picture",
    content: (
      <>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Mechanistic Insights & Future Directions</h2>
          <p className="text-lg mb-4">
            This new testing capability offers more than just a number; it reveals deeper connections between aging pathways.
          </p>
        </div>

        <KeyTakeaway>
          <h3 className="font-semibold mb-2">Overall Picture Shift:</h3>
          <p>We can now directly target and track the <strong>Homeostasis (H)</strong> pillar of SHIFT. By reducing senescent cell burden, we directly mitigate a primary driver of the <strong>Inflammation (I)</strong> pillar, breaking the vicious cycle of "inflammaging."</p>
        </KeyTakeaway>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
            <h3 className="font-semibold text-purple-800 mb-3">Novel Insight #1</h3>
            <h4 className="font-medium text-purple-700 mb-2">The mTOR-Immunity Link</h4>
            <p className="text-purple-700 text-sm">
              The test measures immune resilience, and our proposed therapy (Rapamycin) directly enhances it. Rapamycin inhibits <strong>mTOR</strong>, a key nutrient-sensing pathway. Inhibiting mTOR not only boosts autophagy to help clear cellular debris but also rejuvenates T-cell function, improving the very "Immune Resilience" score we are measuring.
            </p>
            <div className="mt-3 p-3 bg-purple-100 rounded">
              <p className="text-xs text-purple-600 italic">
                The test and therapy are mechanistically synergistic
              </p>
            </div>
          </div>

          <div className="bg-indigo-50 p-6 rounded-lg border border-indigo-200">
            <h3 className="font-semibold text-indigo-800 mb-3">Novel Insight #2</h3>
            <h4 className="font-medium text-indigo-700 mb-2">Pathological vs. Physiological Senescence</h4>
            <p className="text-indigo-700 text-sm">
              The test allows us to distinguish chronic, pathological senescence (high p16, low resilience) from acute, physiological senescence that occurs during wound healing (high p16, <em>high</em> resilience). The <strong>p53/p16 pathways</strong> are our damage sensors, but the immune markers tell us if the cleanup crew is working.
            </p>
            <div className="mt-3 p-3 bg-indigo-100 rounded">
              <p className="text-xs text-indigo-600 italic">
                Prevents inappropriate senolytic use during repair processes
              </p>
            </div>
          </div>
        </div>

        <InfoBox>
          <h3 className="font-semibold mb-2">The Future is Multi-Modal:</h3>
          <p>This T-cell proxy is our best tool now, but expect future assays measuring cell-free DNA from senescent cells and broader SASP panels to give us an even more complete picture.</p>
        </InfoBox>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
            <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <h4 className="font-medium text-green-800 mb-1">Mechanistic Precision</h4>
            <p className="text-xs text-green-700">mTOR inhibition bridges sensing and homeostasis</p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
            <CheckCircle className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <h4 className="font-medium text-blue-800 mb-1">Clinical Differentiation</h4>
            <p className="text-xs text-blue-700">Separate pathological from physiological senescence</p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 text-center">
            <CheckCircle className="h-8 w-8 text-purple-600 mx-auto mb-2" />
            <h4 className="font-medium text-purple-800 mb-1">Future Expansion</h4>
            <p className="text-xs text-purple-700">Multi-modal monitoring coming soon</p>
          </div>
        </div>

        <KeyTakeaway>
          Understanding these mechanistic connections allows clinicians to move beyond symptomatic treatment to address fundamental aging processes with precision and measurable outcomes.
        </KeyTakeaway>
      </>
    ),
  },
  {
    title: "SHIFT Framework Integration Summary",
    content: (
      <>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Holistic Clinical Protocol Summary</h2>
          <p className="text-lg mb-4">
            SapereX integration transforms the SHIFT framework from conceptual to measurable, creating actionable protocols for each pillar.
          </p>
        </div>

        {/* SHIFT Framework Table */}
        <div className="overflow-x-auto mb-6">
          <table className="w-full border-collapse border border-slate-300 text-sm">
            <thead>
              <tr className="bg-slate-100">
                <th className="border border-slate-300 p-3 text-left font-semibold text-slate-900">SHIFT Pillar</th>
                <th className="border border-slate-300 p-3 text-left font-semibold text-slate-900">Core Focus</th>
                <th className="border border-slate-300 p-3 text-left font-semibold text-slate-900">Key Biomarkers</th>
                <th className="border border-slate-300 p-3 text-left font-semibold text-slate-900">High-Impact Interventions</th>
                <th className="border border-slate-300 p-3 text-left font-semibold text-slate-900">Integration Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-yellow-50">
                <td className="border border-slate-300 p-3 font-semibold">Sensing</td>
                <td className="border border-slate-300 p-3">Nutrient & danger sensing</td>
                <td className="border border-slate-300 p-3">HOMA-IR, IGF-1, β-hydroxybutyrate</td>
                <td className="border border-slate-300 p-3">Time-restricted eating, Metformin, GLP-1 RAs</td>
                <td className="border border-slate-300 p-3">mTOR inhibition supports both Sensing and Homeostasis</td>
              </tr>
              <tr className="bg-blue-50">
                <td className="border border-slate-300 p-3 font-semibold">Homeostatic Defence</td>
                <td className="border border-slate-300 p-3">Autophagy, proteostasis, cellular cleanup</td>
                <td className="border border-slate-300 p-3">LC3-II/LC3-I, p62, VO2max</td>
                <td className="border border-slate-300 p-3">Rapamycin, Spermidine, Zone-2 exercise</td>
                <td className="border border-slate-300 p-3 font-semibold bg-blue-100">SapereX: p16 + Immune Resilience = actionable stratification</td>
              </tr>
              <tr className="bg-red-50">
                <td className="border border-slate-300 p-3 font-semibold">Inflammation</td>
                <td className="border border-slate-300 p-3">Chronic inflammatory burden</td>
                <td className="border border-slate-300 p-3">hs-CRP, IL-6, TNF-α, GlycA</td>
                <td className="border border-slate-300 p-3">Omega-3, Curcumin, Senolytics</td>
                <td className="border border-slate-300 p-3">Senescence burden (p16) directly drives "inflammaging"</td>
              </tr>
              <tr className="bg-green-50">
                <td className="border border-slate-300 p-3 font-semibold">Fibrosis</td>
                <td className="border border-slate-300 p-3">Tissue stiffening prevention</td>
                <td className="border border-slate-300 p-3">Elastography, TGF-β1, P3NP</td>
                <td className="border border-slate-300 p-3">Losartan, Pirfenidone, BPC-157</td>
                <td className="border border-slate-300 p-3">Use tissue-specific markers when needed</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="border border-slate-300 p-3 font-semibold">Timekeeping</td>
                <td className="border border-slate-300 p-3">Circadian and genomic clocks</td>
                <td className="border border-slate-300 p-3">DNAm GrimAge, Sleep efficiency</td>
                <td className="border border-slate-300 p-3">Morning light, Timed feeding, Melatonin</td>
                <td className="border border-slate-300 p-3">Align interventions with circadian phase</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="bg-slate-50 p-6 rounded-lg mb-6">
          <h3 className="text-xl font-semibold text-slate-900 mb-4">Protocol Integration Highlights</h3>
          <ul className="space-y-3 text-slate-700">
            <li className="flex items-start">
              <CheckCircle className="text-green-600 h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
              <div>
                <strong>SapereX anchors the Homeostatic Defence pillar:</strong>
                <ul className="mt-1 ml-4 space-y-1 text-sm">
                  <li>• Senescence Load (p16 "Molecular Age") and Immune Resilience stratify patients</li>
                  <li>• Closed-loop: Baseline → Stratify → Intervene → Re-test at 12 weeks</li>
                </ul>
              </div>
            </li>
            <li className="flex items-start">
              <CheckCircle className="text-green-600 h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
              <span><strong>Synergy across pillars:</strong> mTOR inhibition bridges Sensing and Homeostasis</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="text-green-600 h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
              <span><strong>Future directions:</strong> Multi-modal monitoring and circadian-aligned therapies</span>
            </li>
          </ul>
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 text-center">Clinical Impact Summary</h3>
          <p className="text-center font-semibold text-gray-900 mb-4">
            Rigorous, protocolized use of SapereX within the SHIFT framework enables precision, mechanism-anchored management of cellular senescence—translating geroscience from theory to bedside.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 mb-1">4</div>
              <div className="text-sm text-gray-600">Patient Phenotypes</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600 mb-1">12</div>
              <div className="text-sm text-gray-600">Week Re-test Cycle</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 mb-1">2</div>
              <div className="text-sm text-gray-600">Dimensional Analysis</div>
            </div>
          </div>
        </div>

        <KeyTakeaway>
          This represents a paradigm shift from reactive disease treatment to proactive healthspan optimization. 
          The integration of SapereX testing with the SHIFT framework provides clinicians with a mechanistically-driven approach to cellular senescence management, 
          moving longevity medicine from theoretical concepts to practical, measurable interventions.
        </KeyTakeaway>
      </>
    ),
  }
];

const MeasuringSenescenceClinically = () => {
  const [, navigate] = useLocation();
  
  useEffect(() => {
    // Load the ElevenLabs AudioNative script
    const script = document.createElement('script');
    script.src = 'https://elevenlabs.io/player/audioNativeHelper.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup: remove the script when component unmounts
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);
  
  return (
    <MentorshipAccessGate>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        <div className="container max-w-7xl mx-auto px-4 py-8 flex-grow">
          <div className="flex items-center justify-between mb-6">
            <Button 
              variant="ghost" 
              className="flex items-center text-blue-600"
              onClick={() => navigate("/mentorship")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Mentorship
            </Button>
          </div>
          
          <div>
            {/* Breadcrumb Navigation */}
            <Breadcrumb className="mb-6">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link href="/mentorship">Mentorship</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link href="/mentorship#longevity-updates">Longevity Updates</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <span className="text-gray-600">Measuring Senescence Clinically</span>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50] mb-4">
              Measuring Senescence Clinically: From Theory to Practice
            </h1>
            <p className="text-[#7F8C8D] mb-6">
              The first CLIA-validated test for clinical senescence monitoring with SapereX: actionable insights for precision longevity medicine.
            </p>
            <div className="flex items-center gap-3 mb-8">
              <div className="flex items-center text-sm text-[#7F8C8D]">
                <FileText className="h-4 w-4 mr-1" />
                <span>Lecture Duration: 4 slides</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-gray-300"></div>
              <div className="flex items-center text-sm text-[#7F8C8D]">
                <Book className="h-4 w-4 mr-1" />
                <span>Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-gray-300"></div>
              <div className="flex items-center text-sm text-[#7F8C8D]">
                <CheckCircle className="h-4 w-4 mr-1" />
                <span>Category: Research Updates</span>
              </div>
            </div>
            
            <Separator />
            
            <Slideshow slides={lectureSlides} />
            
            {/* ElevenLabs Audio Player */}
            <div className="mt-8 mb-8">
              <div 
                id="elevenlabs-audionative-widget" 
                data-height="90" 
                data-width="100%" 
                data-frameborder="no" 
                data-scrolling="no" 
                data-publicuserid="71e64043f08896c52706cd1491810085e757b2c5cb2032694b73459848ce62de" 
                data-playerurl="https://elevenlabs.io/player/index.html" 
                data-projectid="dv4GFFi8sogU1JphAQgp"
              >
                Loading the <a href="https://elevenlabs.io/text-to-speech" target="_blank" rel="noopener">Elevenlabs Text to Speech</a> AudioNative Player...
              </div>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    </MentorshipAccessGate>
  );
};

export default MeasuringSenescenceClinically;